import type { TagSheetAd, TagSheetParseResult } from '../../types';
import { transformAdForLineItem } from '../../utils/ads';
import { UNMAPPED_GROUP_ID } from '../constants';

type FindAndRemoveAdResult = {
  updatedResult: TagSheetParseResult;
  removedAd: TagSheetAd | null;
};

export const findAndRemoveAd = (parseResult: TagSheetParseResult, adId: string): FindAndRemoveAdResult => {
  const unmappedIndex = parseResult.unmappedAds.findIndex((ad) => ad.id === adId);

  if (unmappedIndex !== -1) {
    const removedAd = parseResult.unmappedAds[unmappedIndex];
    const updatedUnmapped = [...parseResult.unmappedAds];

    updatedUnmapped.splice(unmappedIndex, 1);

    return {
      updatedResult: { ...parseResult, unmappedAds: updatedUnmapped },
      removedAd,
    };
  }

  for (let i = 0; i < parseResult.lineItems.length; i++) {
    const li = parseResult.lineItems[i];
    const adIndex = li.tagSheetAds.findIndex((ad) => ad.id === adId);

    if (adIndex !== -1) {
      const removedAd = li.tagSheetAds[adIndex];
      const updatedAds = [...li.tagSheetAds];

      updatedAds.splice(adIndex, 1);

      const updatedLineItems = [...parseResult.lineItems];

      updatedLineItems[i] = { ...li, tagSheetAds: updatedAds };

      return {
        updatedResult: { ...parseResult, lineItems: updatedLineItems },
        removedAd,
      };
    }
  }

  return { updatedResult: parseResult, removedAd: null };
};

export const getUpdatedParseResultAfterDrop = (
  prevParseResult: TagSheetParseResult,
  adId: string,
  targetGroupId: string
): TagSheetParseResult => {
  const { updatedResult, removedAd } = findAndRemoveAd(prevParseResult, adId);

  if (!removedAd) return prevParseResult;

  if (targetGroupId === UNMAPPED_GROUP_ID) {
    return {
      ...updatedResult,
      unmappedAds: [...updatedResult.unmappedAds, removedAd],
    };
  }

  const lineItemIndex = updatedResult.lineItems.findIndex((li) => li.id === targetGroupId);

  if (lineItemIndex === -1) return prevParseResult;

  const updatedLineItems = [...updatedResult.lineItems];
  const targetLineItem = updatedLineItems[lineItemIndex];
  const transformedAd = transformAdForLineItem(removedAd, targetLineItem);

  updatedLineItems[lineItemIndex] = {
    ...targetLineItem,
    tagSheetAds: [...targetLineItem.tagSheetAds, transformedAd],
  };

  return { ...updatedResult, lineItems: updatedLineItems };
};
